import {
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useConstant from "use-constant";
import styled from "styled-components";
import {
  space,
  color,
  layout,
  border,
  LayoutProps,
  BorderProps,
  ColorProps,
  SpaceProps,
} from "styled-system";

import { PanelProps, ButtonTabProps, TabsProps } from "./tabs.type";

const TabsState = createContext<any | undefined>(undefined);
const Elements = createContext<{ tabs: number; panels: number } | undefined>(
  undefined
);

function useTabContext() {
  const context = useContext(TabsState);
  if (context === undefined) {
    throw new Error("useTabContext must be used within a TabProvider");
  }
  return context;
}

function useElementsContext() {
  const context = useContext(Elements);
  if (context === undefined) {
    throw new Error("useElementsContext must be used within a ElementProvider");
  }
  return context;
}

export const useTabState = () => {
  const [activeIndex, setActive] = useTabContext();
  const elements = useElementsContext();

  const tabIndex = useConstant(() => {
    const currentIndex = elements.tabs;
    elements.tabs += 1;

    return currentIndex;
  });

  const onClick = useConstant(() => () => setActive(tabIndex));

  const state = useMemo(
    () => ({
      isActive: activeIndex === tabIndex,
      onClick,
      activeIndex,
    }),
    [activeIndex, onClick, tabIndex]
  );

  return state;
};

export const usePanelState = () => {
  const [activeIndex] = useTabContext();
  const elements = useElementsContext();

  const panelIndex = useConstant(() => {
    const currentIndex = elements.panels;
    elements.panels += 1;

    return currentIndex;
  });

  return panelIndex === activeIndex;
};

export const Tabs = ({ activeTabIndex, children }: TabsProps) => {
  const state = useState(activeTabIndex || 0);
  const elements = useConstant(() => ({ tabs: 0, panels: 0 }));

  const child = typeof children === "function" ? children(state[0]) : children;

  return (
    <Elements.Provider value={elements}>
      <TabsState.Provider value={state}>{child}</TabsState.Provider>
    </Elements.Provider>
  );
};

export const Tab = ({ children }: TabsProps) => {
  const state = useTabState();

  if (typeof children === "function") {
    return children(state);
  }

  return isValidElement(children) ? cloneElement(children, state) : children;
};

export const Panel = ({ active, children }: PanelProps) => {
  const isActive = usePanelState();
  const showPanel = isActive || active;

  return showPanel && isValidElement(children) ? cloneElement(children) : null;
};

const StyledTab = styled.button<
  ColorProps & LayoutProps & SpaceProps & BorderProps
>`
  border: none;
  background: transparent;
  padding: 16px;
  outline: none;
  cursor: pointer;
  box-sizing: border-box;
  ${color}
  ${space}
  ${layout}
  ${border}
`;

export const ButtonTab = ({
  isActive,
  onClick,
  activeIndex = 0,
  children,
  ...props
}: ButtonTabProps) => {
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!!ref.current && isActive === true) {
      if (ref.current.parentElement) {
        const style = window.getComputedStyle(ref.current);
        const parentStyle = window.getComputedStyle(ref.current.parentElement);

        const width =
          ref.current.clientWidth +
          parseFloat(style.marginLeft) +
          parseFloat(style.marginRight);
        const options = {
          left:
            activeIndex * width -
            ref.current.parentElement.clientWidth / 2 +
            width / 2 +
            parseFloat(parentStyle.paddingLeft),
        };
        ref.current.parentElement.scrollLeft = options.left;
      }
    }
  }, [activeIndex, isActive]);

  return (
    // @ts-ignore
    <StyledTab onClick={onClick} ref={ref} {...props}>
      {children}
    </StyledTab>
  );
};
